import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import {
  canAskVatRegistry,
  vatRegistrationKeys,
  vatRegistrationQueryOptions,
} from "Api/Queries/Party/useVatRegistrationQuery";
import type { BusinessInformationStepSchema } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationStepContent";
import { VatStatus } from "Infrastructure/Api/Api";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { useState } from "react";
import type { UseFormGetValues, UseFormSetValue } from "react-hook-form";

type Props = {
  getValues: UseFormGetValues<BusinessInformationStepSchema>;
  setValue: UseFormSetValue<BusinessInformationStepSchema>;
};

/**
 * Keeps the form-only `vatRegistryStatus` of the business-information step in line with the numbers in
 * the form: asks the VAT register (a preview — the save verifies again) and, for an empty SK field, fills
 * in the IČ DPH the DIČ implies. Everything runs from field events; nothing watches values in an effect.
 */
export const useVatRegistryLookup = ({ getValues, setValue }: Props) => {
  const queryClient = useQueryClient();
  // While the user is in the field, nothing about its (possibly half-edited) value should be shown under it.
  const [isVatNumberFocused, setIsVatNumberFocused] = useState(false);
  // Only gates what is shown under the field, never the submit: the save asks the register itself, and
  // disabling the button mid-lookup would swallow the very click that blurred the field.
  const isCheckingVatRegistry =
    useIsFetching({ queryKey: vatRegistrationKeys.all }) > 0;

  // Asks the VAT register about the numbers now in the form and records its answer — unless the user has
  // moved on to other numbers meanwhile (a late answer about old input must not overwrite a newer one).
  // Anything that is not an answer (skipped, 400, network) leaves the question open.
  const refreshVatRegistration = async ({
    shouldFillVatNumber,
  }: {
    shouldFillVatNumber: boolean;
  }) => {
    const {
      region: askedRegion,
      taxNumber,
      vatNumber: askedVatNumber,
    } = getValues();
    const askedTaxNumber = taxNumber ?? "";

    // The schema types region as always set; until the user picks one it is not.
    const registration =
      askedRegion != null &&
      canAskVatRegistry(askedRegion, askedTaxNumber, askedVatNumber)
        ? await queryClient
            .fetchQuery(
              vatRegistrationQueryOptions(
                askedRegion,
                askedTaxNumber,
                askedVatNumber,
              ),
            )
            .catch(() => null)
        : null;

    const current = getValues();
    if (
      current.region !== askedRegion ||
      (current.taxNumber ?? "") !== askedTaxNumber ||
      current.vatNumber !== askedVatNumber
    ) {
      return;
    }

    setValue("vatRegistryStatus", registration?.vatStatus ?? VatStatus.Unknown);

    if (
      shouldFillVatNumber &&
      registration &&
      askedVatNumber === NO_VAT_NUMBER &&
      registration.vatNumber !== NO_VAT_NUMBER
    ) {
      setValue("vatNumber", registration.vatNumber);
    }
  };

  // A typed IČ DPH is asked about once it has the full shape; a half-typed or cleared one is no answer yet,
  // so the status shown never describes a number that is no longer in the field.
  const handleVatNumberChange = () => {
    const { region, taxNumber, vatNumber: typed } = getValues();

    if (
      typed === NO_VAT_NUMBER ||
      region == null ||
      !canAskVatRegistry(region, taxNumber ?? "", typed)
    ) {
      setValue("vatRegistryStatus", VatStatus.Unknown);
      return;
    }

    void refreshVatRegistration({ shouldFillVatNumber: false });
  };

  const handleVatNumberFocus = () => {
    setIsVatNumberFocused(true);
  };

  // A field left empty is asked about on blur, not on every keystroke: the DIČ may imply the number and the
  // register's word puts it back (the save would do the same) — but only once the user is done editing, so
  // clearing "SK…" to type a VAT-group number is not undone under their fingers by the cached answer.
  const handleVatNumberBlur = () => {
    setIsVatNumberFocused(false);

    if (getValues("vatNumber") === NO_VAT_NUMBER) {
      void refreshVatRegistration({ shouldFillVatNumber: true });
    }
  };

  return {
    isCheckingVatRegistry,
    isVatNumberFocused,
    refreshVatRegistration,
    handleVatNumberChange,
    handleVatNumberFocus,
    handleVatNumberBlur,
  };
};
